/**
 * Prospect pipeline vocabulary.
 *
 * Stages are milestones (where the owner is), never activities (what we did).
 * The old list ("Letter Sent", "Email Sent", "Phone Call") described our own
 * actions and was blank on 49 of 70 rows by September 2026. Nurture is NOT a
 * stage: a parked prospect keeps its stage and carries status = On Hold with
 * a revisit date, the same triple clients use.
 *
 * Priority is manual. The rule that forced Hot on reaching "Phone Call" was
 * deleted on 2026-09-02; nothing overwrites a person's read.
 */
export const PROSPECT_STAGES = [
  "Identified",
  "Contacted",
  "In conversation",
  "MVA delivered",
  "Engagement proposed",
] as const;
export type ProspectStage = (typeof PROSPECT_STAGES)[number];

export const PROSPECT_NURTURE_STATUS = "On Hold" as const;

export function prospectStageIndex(stage: string | null | undefined): number {
  return (PROSPECT_STAGES as readonly string[]).indexOf(stage ?? "");
}

export function isProspectStage(stage: string | null | undefined): stage is ProspectStage {
  return prospectStageIndex(stage) !== -1;
}

/**
 * One-time mapping from the retired stage list. Activity stages collapse to
 * the milestone they imply; the old "Nurture" stage becomes the parked
 * status with no revisit date (a person picks one when they next look).
 */
export function migrateProspectStage(old: string | null | undefined): { stage: ProspectStage; park: boolean } {
  if (isProspectStage(old)) return { stage: old, park: false };
  switch (old) {
    case "Letter Sent":
    case "Email Sent":
      return { stage: "Contacted", park: false };
    case "Phone Call":
    case "Meeting":
      return { stage: "In conversation", park: false };
    case "MVA":
      return { stage: "MVA delivered", park: false };
    case "Nurture":
      return { stage: "Identified", park: true };
    default:
      return { stage: "Identified", park: false };
  }
}

/** `companies.interestLevel`, hottest first. "Cool" renders as "Cold". */
export const PROSPECT_PRIORITIES = ["Hot", "Warm", "Cool"] as const;
export type ProspectPriority = (typeof PROSPECT_PRIORITIES)[number];

export const PROSPECT_PRIORITY_LABELS: Record<string, string> = { Hot: "Hot", Warm: "Warm", Cool: "Cold" };

/** Sort key for hot-to-cold ordering. Unset sorts with Cool. */
export function prospectPriorityIndex(level: string | null | undefined): number {
  const i = (PROSPECT_PRIORITIES as readonly string[]).indexOf(level ?? "");
  return i === -1 ? PROSPECT_PRIORITIES.length - 1 : i;
}

/**
 * Title of the stage_change activity convertProspect writes when a prospect
 * becomes a Client. The COI strip and the dashboard's Mandates YTD tile both
 * count conversions by it, so it is defined once.
 */
export const CONVERTED_TO_CLIENT_TITLE = "Converted to Client";
